import React, { useContext, useState, useRef, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import classNames from 'classnames';
import SimpleReactValidator from 'simple-react-validator';
import { ContractContext } from '../../../components/contexts/ContractContext';
import styles from './CustomerDetails.module.css';
import { ReactComponent as Chevron } from '../../../assets/images/arrow-cheveron-down.svg';
import { AddressForm, StaticAddress } from './CustomerAddressForm';
import { ContactInfoForm, StaticContactInfo } from '../../../components/shared/ContactInformation/ContactInformation';
import RecommendedAddressModal from '../../Sales/RecommendedAddressModal';
import IncorrectAddressModal from '../../Sales/IncorrectAddressModal';
import { verifyAddress, VerificationResponseCodes } from '../../api/addressverification';
import { useLocation } from 'react-router';
import { parse } from 'query-string';
import getClientContent from '../../_BrandingProvider/brandContent';
import { useEnvironment } from '../../_ReactHooks/useEnvironment';
import SpinnerButton from '../../shared/SpinnerButton/SpinnerButton';

const CustomerDetails = ({ history }) => {
    const { editServiceAddress } = useEnvironment();
    const [editContactInfo, setEditContactInfo] = useState(false);
    const [states, setStates] = useState([]);
    const [disableSaveButton, setDisableSaveButton] = useState(true);
    const [validationFired, setValidationFired] = useState(false);
    const location = useLocation();
    const [token, setToken] = useState(parse(location.search)?.id || parse(location.search)?.token);
    const [, forceUpdate] = useState();
    const context = useContext(ContractContext);
    const contextClientId = context?.data?.clientId || context?.data?.contract?.clientId || 'aig';
    const isEditAccount = getClientContent(contextClientId)?.variableText?.sections?.isAccountDetailsEdit;
    const salesPhone = getClientContent(contextClientId)?.variableText?.sales?.phone ?? '';
    const { party, contract } = JSON.parse(sessionStorage.getItem('appState'));
    const clientId = party?.clientId;
    const [activeTabView, setActiveTabView] = useState(location?.state?.activeOverviewTab || 'fileClaim');
    const [activePlanView, setActivePlanView] = useState(location?.state?.view || 'kanban');
    const [showSuggestedAddressModal, setShowSuggestedAddressModal] = useState(false);
    const [showInvalidAddressModal, setShowInvalidAddressModal] = useState(false);
    const [showServiceAddress, setShowServiceAddress] = useState(false);
    const [showMailingAddress, setShowMailingAddress] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [recommendedAddress, setRecommendedAddress] = useState({});

    const validator = useRef(
        new SimpleReactValidator({
            element: (message) => (
                <div
                    className='errorMsg'
                    aria-live='polite'
                >
                    {message.charAt(0).toUpperCase() + message.slice(1)}
                </div>
            ),
            messages: {
                phone: 'Please enter a valid phone number.'
            },
            validators: {
                valid_phone: {
                    message: 'Please enter a valid phone number.',
                    rule: (val, params, validator) => {
                        return !new RegExp('^([0-9])\\1{9}$').test(val.match(/\d+/g).join(''));
                    }
                }
            }
        })
    );

    useEffect(() => {
        if (!editContactInfo) {
            setFormData(getData());
        }
    }, [editContactInfo]);

    const getData = () => {
        const alternatePhone = party.partyPhoneModel.find(({ phoneTypeId }) => phoneTypeId === 1);
        const primaryPhone = party.partyPhoneModel.find(({ phoneTypeId }) => phoneTypeId === 2);
        const mailingAddress = party.partyAddressModel.find(({ addressTypeId }) => addressTypeId === 3);
        const serviceAddress = party.partyAddressModel?.find(({ addressTypeId }) => addressTypeId === 2);

        return {
            name: party.partyName === null ? party.firstName.concat(' ', party.lastName) : party.partyName,
            firstname: party.firstName,
            lastName: party.lastName,
            email: party.partyEmailModel[0].address,
            alternatePhone: alternatePhone?.number ?? '',
            mobilePhone: primaryPhone?.number ?? '',
            billingAddress: {
                address1: party.billingAddress?.address1 ?? '',
                address2: party.billingAddress?.address2 ?? '',
                city: party.billingAddress?.city ?? '',
                state: party.billingAddress?.state ?? '',
                stateId: party.billingAddress?.stateId ?? '',
                zip: party.billingAddress?.zip ?? ''
            },
            existingServiceAddress: {
                address1: party.serviceAddress?.address1 ?? '',
                address2: party.serviceAddress?.address2 ?? '',
                city: party.serviceAddress?.city ?? '',
                state: party.serviceAddress?.state ?? '',
                stateId: party.serviceAddress?.stateId ?? '',
                zip: party.serviceAddress?.zip ?? ''
            },
            existingMailingAddress: {
                address1: party.shippingAddress?.address1 ?? '',
                address2: party.shippingAddress?.address2 ?? '',
                city: party.shippingAddress?.city ?? '',
                state: party.shippingAddress?.state ?? '',
                stateId: party.shippingAddress?.stateId ?? '',
                zip: party.shippingAddress?.zip ?? ''
            },
            serviceAddress: {
                address1: serviceAddress?.address1 ?? '',
                address2: serviceAddress?.address2 ?? '',
                city: serviceAddress?.city ?? '',
                state: serviceAddress?.state ?? '',
                stateId: serviceAddress?.stateId ?? '',
                zip: serviceAddress?.zip ?? ''
            },
            mailingAddress: {
                address1: mailingAddress?.address1 ?? '',
                address2: mailingAddress?.address2 ?? '',
                city: mailingAddress?.city ?? '',
                state: mailingAddress?.state ?? '',
                stateId: mailingAddress?.stateId ?? '',
                zip: mailingAddress?.zip ?? ''
            }
        };
    };

    const [formData, setFormData] = useState(() => getData());

    const handleInputChange = (e, type) => {
        setDisableSaveButton(false);
        let { name, value } = e.target;

        if (type === 'Mailing Address') {
            setFormData({
                ...formData,
                mailingAddress: {
                    ...formData.mailingAddress,
                    [name]: value
                }
            });
        } else if (type === 'Service Address') {
            setFormData({
                ...formData,
                serviceAddress: {
                    ...formData.serviceAddress,
                    [name]: value
                }
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleStateDropDown = (e, type) => {
        setDisableSaveButton(false);
        const selectedState = Array.isArray(states.state)
            ? states.state?.find(({ data }) => data === e)?.abbreviation
            : 0;

        if (type === 'Service Address') {
            setFormData({
                ...formData,
                serviceAddress: {
                    ...formData.serviceAddress,
                    state: selectedState,
                    stateId: e
                }
            });
        } else {
            setFormData({
                ...formData,
                mailingAddress: {
                    ...formData.mailingAddress,
                    state: selectedState,
                    stateId: e
                }
            });
        }
    };

    useEffect(() => {
        fetch('api/party/states/'.concat(contract.clientId))
            .then((res) => res.json())
            .then((data) => {
                const result = [];
                data.map((s) => {
                    result.push({
                        data: s.stateId,
                        display: s.name,
                        abbreviation: s.abbreviation
                    });
                    setStates({ state: result });
                });
            });
    }, []);

    const handleContactInfo = () => {
        setValidationFired(true);
        const checkPhoneError = formData.alternatePhone !== '' || formData.mobilePhone !== '';
        if (validator.current.allValid() && checkPhoneError) {
            setIsLoading(true);
            setValidationFired(false);

            const addressObj = {
                address1: formData.mailingAddress?.address1 ?? '',
                address2: formData.mailingAddress?.address2 ?? '',
                city: formData.mailingAddress?.city ?? '',
                state: formData.mailingAddress?.state ?? '',
                stateId: formData.mailingAddress?.stateId ?? '',
                zip: formData.mailingAddress?.zip ?? ''
            };
            verifyAddress(addressObj, contextClientId, onMailingAddressVerified);
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };

    const onMailingAddressVerified = (result) => {
        let stateInfo = '';
        const verifiedAddress = result.returnedAddress;

        if (
            result.code !== VerificationResponseCodes.error
            && result.code !== VerificationResponseCodes.invalidAddress
            && result.code !== VerificationResponseCodes.thirdpartyerror
        ) {
            stateInfo = states.state.filter((el) => {
                return el.abbreviation === verifiedAddress?.stateAbbreviation;
            });
        }
        if (result.code === VerificationResponseCodes.addressVerified) {
            setShowServiceAddress(false);
            setShowMailingAddress(false);
            setShowSuggestedAddressModal(false);

            const addressToSave = {
                address1: verifiedAddress?.address1,
                address2: verifiedAddress?.address2 || verifiedAddress?.suite,
                city: verifiedAddress?.city,
                zip: verifiedAddress?.zip,
                state: verifiedAddress?.stateAbbreviation,
                stateId: stateInfo?.length > 0 ? stateInfo[0]?.data : formData.mailingAddress?.stateId
            };
            saveVerifiedAddress(addressToSave, 'Mailing Address');
        } else if (result.code === VerificationResponseCodes.addressChanged) {
            setRecommendedAddress({
                mailingAddress: {
                    address1: verifiedAddress?.address1,
                    address2: verifiedAddress?.address2 || verifiedAddress?.suite,
                    city: verifiedAddress?.city,
                    zip: verifiedAddress?.zip,
                    state: verifiedAddress?.stateAbbreviation,
                    stateId: stateInfo?.length > 0 ? stateInfo[0]?.data : formData.mailingAddress?.stateId
                }
            });
            setShowServiceAddress(false);
            setShowMailingAddress(true);
            setShowSuggestedAddressModal(true);
        } else {
            setShowServiceAddress(false);
            setShowMailingAddress(true);
            setShowSuggestedAddressModal(false);
            setShowInvalidAddressModal(true);
        }
    };

    const onServiceAddressVerified = (result) => {
        let stateInfo = '';
        const verifiedAddress = result.returnedAddress;

        if (
            result.code !== VerificationResponseCodes.error
            && result.code !== VerificationResponseCodes.invalidAddress
            && result.code !== VerificationResponseCodes.thirdpartyerror
        ) {
            stateInfo = states.state.filter((el) => {
                return el.abbreviation === verifiedAddress?.stateAbbreviation;
            });
        }
        if (result.code === VerificationResponseCodes.addressVerified) {
            setShowServiceAddress(false);
            setShowMailingAddress(false);
            setShowSuggestedAddressModal(false);

            const addressToSave = {
                address1: verifiedAddress?.address1,
                address2: verifiedAddress?.address2 || verifiedAddress?.suite,
                city: verifiedAddress?.city,
                zip: verifiedAddress?.zip,
                state: verifiedAddress?.stateAbbreviation,
                stateId: stateInfo?.length > 0 ? stateInfo[0]?.data : formData.serviceAddress?.stateId
            };
            saveVerifiedAddress(addressToSave, 'Service Address');
        } else if (result.code === VerificationResponseCodes.addressChanged) {
            setRecommendedAddress({
                serviceAddress: {
                    address1: verifiedAddress?.address1,
                    address2: verifiedAddress?.address2 || verifiedAddress?.suite,
                    city: verifiedAddress?.city,
                    zip: verifiedAddress?.zip,
                    state: verifiedAddress?.stateAbbreviation,
                    stateId: stateInfo?.length > 0 ? stateInfo[0]?.data : formData.serviceAddress?.stateId
                }
            });
            setShowServiceAddress(true);
            setShowMailingAddress(false);
            setShowSuggestedAddressModal(true);
        } else {
            setShowServiceAddress(true);
            setShowMailingAddress(false);
            setShowSuggestedAddressModal(false);
            setShowInvalidAddressModal(true);
        }
    };

    const saveVerifiedAddress = (addressToSave, addressType) => {
        onCloseAddressModal();

        if (addressType === 'Mailing Address') {
            formData.mailingAddress.address1 = addressToSave?.address1;
            formData.mailingAddress.address2 = addressToSave?.address2;
            formData.mailingAddress.city = addressToSave?.city;
            formData.mailingAddress.state = addressToSave?.state;
            formData.mailingAddress.stateId = addressToSave?.stateId;
            formData.mailingAddress.zip = addressToSave?.zip;

            if (editServiceAddress) {
                const addressObj = {
                    address1: formData.serviceAddress?.address1 ?? '',
                    address2: formData.serviceAddress?.address2 ?? '',
                    city: formData.serviceAddress?.city ?? '',
                    state: formData.serviceAddress?.state ?? '',
                    stateId: formData.serviceAddress?.stateId ?? '',
                    zip: formData.serviceAddress?.zip ?? ''
                };
                verifyAddress(addressObj, contextClientId, onServiceAddressVerified);
            } else {
                onContinueSaveAction();
            }
        } else {
            formData.serviceAddress.address1 = addressToSave?.address1;
            formData.serviceAddress.address2 = addressToSave?.address2;
            formData.serviceAddress.city = addressToSave?.city;
            formData.serviceAddress.state = addressToSave?.state;
            formData.serviceAddress.stateId = addressToSave?.stateId;
            formData.serviceAddress.zip = addressToSave?.zip;

            onContinueSaveAction();
        }
    };

    const onContinueSaveAction = () => {
        let partyPhone = party?.partyPhoneModel;
        let partyAddress = party?.partyAddressModel;
        let partyEmail = party.partyEmailModel;
        const alternatePhone = partyPhone.find(({ phoneTypeId }) => phoneTypeId === 1);
        const primaryPhone = partyPhone.find(({ phoneTypeId }) => phoneTypeId === 2);
        const mailingAddress = partyAddress.find(({ addressTypeId }) => addressTypeId === 3);

        mailingAddress.address1 = formData.mailingAddress?.address1;
        mailingAddress.address2 = formData.mailingAddress?.address2;
        mailingAddress.city = formData.mailingAddress?.city;
        mailingAddress.state = formData.mailingAddress?.state;
        mailingAddress.stateId = formData.mailingAddress?.stateId;
        mailingAddress.zip = formData.mailingAddress?.zip;

        if (editServiceAddress) {
            const serviceAddress = partyAddress.find(({ addressTypeId }) => addressTypeId === 2);

            serviceAddress.address1 = formData.serviceAddress?.address1;
            serviceAddress.address2 = formData.serviceAddress?.address2;
            serviceAddress.city = formData.serviceAddress?.city;
            serviceAddress.state = formData.serviceAddress?.state;
            serviceAddress.stateId = formData.serviceAddress?.stateId;
            serviceAddress.zip = formData.serviceAddress?.zip;
        }
        if (primaryPhone) {
            primaryPhone.number = formData.mobilePhone;
        }
        if (formData.alternatePhone !== '') {
            if (alternatePhone) {
                alternatePhone.number = formData.alternatePhone;
            } else {
                const alternateNumber = {
                    partyId: party.partyId,
                    isPrimary: false,
                    phoneTypeId: 1,
                    number: formData.alternatePhone
                };
                partyPhone.push(alternateNumber);
            }
        }
        partyEmail.find(({ isPrimary }) => isPrimary === true).address = formData.email;
        let firstname = formData.name.split(' ')[0];
        let lastname = formData.name.split(' ').length > 1 ? formData.name.split(' ')[1] : '';
        fetch(`api/party/PartyAddressUpdate/${party.partyId}/${contract.clientId}/${contract.contractId}`, {
            method: 'put',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                partyId: party.partyId,
                firstName: firstname,
                lastName: lastname,
                partyName: firstname + ' ' + lastname,
                partyAddressModel: partyAddress,
                partyPhoneModel: partyPhone,
                partyEmailModel: partyEmail
            })
        }).then((res) => {
            if (res.ok) {
                res.json().then((data) => {
                    let editedData = JSON.parse(sessionStorage.getItem('appState'));
                    editedData.party = data;
                    sessionStorage.setItem('appState', JSON.stringify(editedData));
                    history.push({
                        pathname: '/planoverview',
                        state: {
                            accountDetailsUpdated: true,
                            updatedPartyName: editedData.party
                        }
                    });
                });
            } else {
                setIsLoading(false);
                console.error(res.status);
            }
        });
    };

    const onCloseAddressModal = () => {
        setShowInvalidAddressModal(false);
        setShowSuggestedAddressModal(false);
    };

    const backToDashboard = () => {
        history.push({
            pathname: '/planoverview',
            search: typeof token == 'undefined' ? null : `?token=${token}`,
            state: {
                view: activePlanView,
                activeOverviewTab: activeTabView
            }
        });
    };

    const onEditAddress = () => {
        setIsLoading(false);
        setShowInvalidAddressModal(false);
        setShowSuggestedAddressModal(false);
    };

    return (
        <div className='container contentWrapper'>
            <Row>
                <Col
                    lg='12'
                    md='12'
                    className='mb-5'
                >
                    <a
                        onClick={() => backToDashboard()}
                        className={classNames(styles.viewLink, 'btnLink')}
                    >
                        <Chevron className={styles.searchIcon} />
                        View Dashboard
                    </a>
                </Col>
            </Row>
            <Card className={styles.accountDetails}>
                <CardHeader className={classNames(styles.borderStyle, 'text-center')}>
                    <h1 className='mb-3 h5'>My Account Details</h1>
                    {!isEditAccount && !editServiceAddress && (
                        <>
                            <p className={classNames(styles.headerText, 'mb-0')}>Incorrect Service Address?</p>
                            <p className={styles.headerText}>Call {salesPhone}</p>
                        </>
                    )}
                </CardHeader>
                <CardBody>
                    {!editContactInfo ? (
                        <>
                            <Row>
                                <Col
                                    lg='6'
                                    md='6'
                                >
                                    <StaticContactInfo
                                        contactInfo={formData}
                                        title={'My Information'}
                                    />
                                </Col>
                                <Col
                                    lg='6'
                                    md='6'
                                >
                                    <fieldset>
                                        <legend className='cardTitle font-weight-bold mb-0'>Mailing Address</legend>
                                        <StaticAddress
                                            addressData={formData.existingMailingAddress}
                                            showName={false}
                                            clientId={clientId}
                                            states={states.state}
                                        />
                                    </fieldset>
                                </Col>
                            </Row>
                            <Row>
                                {!isEditAccount && (
                                    <Col
                                        lg='6'
                                        md='6'
                                    >
                                        <fieldset>
                                            <legend className='cardTitle font-weight-bold mb-0'>Service Address</legend>
                                            <StaticAddress
                                                addressData={formData.existingServiceAddress}
                                                showName={false}
                                                clientId={clientId}
                                                states={states.state}
                                            />
                                        </fieldset>
                                    </Col>
                                )}
                                <Col
                                    lg='6'
                                    md='6'
                                    className='mt-5'
                                >
                                    <button
                                        className='btn btn-secondary'
                                        onClick={() => setEditContactInfo(true)}
                                    >
                                        Edit Info
                                    </button>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>
                            <Row>
                                <Col
                                    lg='12'
                                    md='12'
                                >
                                    <ContactInfoForm
                                        validator={validator}
                                        contactInfo={{
                                            name: formData.name,
                                            email: formData.email,
                                            phone: {
                                                mobilePhone: formData.mobilePhone,
                                                alternatePhone: formData.alternatePhone
                                            }
                                        }}
                                        title={'Contact Information'}
                                        validationFired={validationFired}
                                        handleInputChange={(e) => handleInputChange(e, 'Contact Information')}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col
                                    lg='12'
                                    md='12'
                                >
                                    <h6>{'Mailing Address'}</h6>
                                    <AddressForm
                                        address={formData.mailingAddress}
                                        addressType='Mailing Address'
                                        states={states.state}
                                        handleInputChange={(e) => handleInputChange(e, 'Mailing Address')}
                                        handleStateDropDown={(e) => handleStateDropDown(e, 'Mailing Address')}
                                        validator={validator.current}
                                        validationFired={validationFired}
                                        clientId={contract.clientId}
                                    />
                                </Col>
                            </Row>
                            {!isEditAccount && (
                                <Row>
                                    <Col
                                        lg='12'
                                        md='12'
                                    >
                                        <h6>{'Service Address'}</h6>
                                        {editServiceAddress ? (
                                            <AddressForm
                                                addressType='Service Address'
                                                address={formData.serviceAddress}
                                                states={states.state}
                                                validator={validator.current}
                                                validationFired={validationFired}
                                                clientId={contract.clientId}
                                                handleInputChange={(e) => handleInputChange(e, 'Service Address')}
                                                handleStateDropDown={(e) => handleStateDropDown(e, 'Service Address')}
                                            />
                                        ) : (
                                            <StaticAddress
                                                addressData={formData.existingServiceAddress}
                                                showName={false}
                                                clientId={clientId}
                                                states={states.state}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            )}
                            <Row>
                                <Col
                                    lg='12'
                                    md='12'
                                    className='d-flex flex-center'
                                >
                                    <button
                                        className='btn btn-secondary mr-4'
                                        onClick={() => setEditContactInfo(false)}
                                    >
                                        Cancel
                                    </button>
                                    <SpinnerButton
                                        type='primary'
                                        text='Save and Close'
                                        ariaLabel='Click to save your data'
                                        spinning={isLoading}
                                        disabled={disableSaveButton || isLoading}
                                        onClick={handleContactInfo}
                                    />
                                </Col>
                            </Row>

                            {showSuggestedAddressModal && (
                                <RecommendedAddressModal
                                    enteredAddress={formData}
                                    suggestedAddress={recommendedAddress}
                                    showServiceAddress={showServiceAddress}
                                    showMailingAddress={showMailingAddress}
                                    showSuggestedAddressModal={showSuggestedAddressModal}
                                    saveVerifiedAddress={saveVerifiedAddress}
                                    hideModal={onEditAddress}
                                />
                            )}

                            {showInvalidAddressModal && (
                                <IncorrectAddressModal
                                    enteredAddress={formData}
                                    showServiceAddress={showServiceAddress}
                                    showMailingAddress={showMailingAddress}
                                    showInvalidAddressModal={showInvalidAddressModal}
                                    saveVerifiedAddress={saveVerifiedAddress}
                                    hideModal={onEditAddress}
                                />
                            )}
                        </>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default CustomerDetails;
